import { Form, Formik } from 'formik';
import { Button, Stack, Dialog, DialogTitle, DialogContent, FormHelperText, Box } from '@mui/material';
import { Field } from 'formik';
import FormTextInput from './FormTextInput';
import CategoryPicker from './CategoryPicker';
import ImagePicker from './ImagePicker';
import LabeledCheckbox from './LabeledCheckbox';
import { ItemType } from '../schema';
import * as Yup from 'yup';

interface ItemTypeFormValues {
    name: string;
    description: string;
    categoryId: string;
    photoUrl?: string;
    consumable: boolean;
}

interface Props {
    itemType?: ItemType;
    onSubmit: (values: ItemTypeFormValues) => Promise<void>;
    onCancel: () => void;
    open?: boolean;
}

const itemTypeFormSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string(),
    categoryId: Yup.string().required('Category is required'),
    consumable: Yup.boolean(),
});

export default function ItemTypeForm({ itemType, onSubmit, onCancel, open = true }: Props) {
    const initialValues: ItemTypeFormValues = {
        name: itemType?.name || '',
        description: itemType?.description || '',
        categoryId: itemType?.category?.id || '',
        photoUrl: itemType?.photoUrl,
        consumable: itemType?.consumable || false,
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            BackdropProps={{
                style: { zIndex: 1400 }, // Higher z-index for backdrop
            }}
            PaperProps={{
                style: { zIndex: 1400 }, // Higher z-index to appear above other dialogs
            }}
        >
            <DialogTitle>{itemType ? 'Edit Item Type' : 'Create Item Type'}</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={initialValues}
                    validationSchema={itemTypeFormSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await onSubmit(values);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Field name="name" component={FormTextInput} label="Name" required fullWidth />
                                <CategoryPicker label="Category" required groupSystemCategories={true} />
                                <Field name="description" component={FormTextInput} label="Description" fullWidth multiline rows={3} />
                                <Field name="photo" component={ImagePicker} label="Photo" />
                                <Box>
                                    <Field name="consumable" component={LabeledCheckbox} label="Consumable" />
                                    <FormHelperText>
                                        Check if this item is used up at events (garbage bags, cleaning supplies, etc.)
                                    </FormHelperText>
                                </Box>
                                <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                                    <Button onClick={onCancel} disabled={isSubmitting}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                                        {itemType ? 'Update' : 'Create'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
